import {
  IHttpResponse,
  ISearchRequest,
  ITCRCampaignCreationRequest,
  ITCRCampaignDetails,
  IUpdateTCRCampaignRequest,
} from '@gr/shared/models';
import useAxios from 'axios-hooks';
import { useEffect, useState } from 'react';
import { NotificationType, useNotifications } from '../../contexts/NotificationContext';
import useProviders from '../../hooks/useProviders';
import useTCRCampaigns from '../../hooks/useTCRCampaigns';
import { Button } from '../shared/Buttons/Button';
import { ButtonVariantEnum } from '../shared/Buttons/types';
import { IDropdownValue } from '../shared/Form/Dropdown';
import { Table } from '../shared/Table/Table';
import { IColumn } from '../shared/Table/types';
import TCRCampaignDetailsPanel from './TCRCampaignDetailsPanel';
import { ITCRCampaignForm, defaultTCRCampaignTableOptions, getColumns, tcrCampaignDropdownOptions } from './types';

const TCRCampaigns = (): JSX.Element => {
  const defaultError = 'An unexpected error occurred when attempting to save the TCR Campaign.';
  const [selectedItem, setSelectedItem] = useState<ITCRCampaignDetails>();
  const [tableOptions, setTableOptions] = useState(defaultTCRCampaignTableOptions);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [forceUpdateWarning, setForceUpdateWarning] = useState('');
  const [pendingUpdateData, setPendingUpdateData] = useState<ITCRCampaignForm>();
  const { addNotification } = useNotifications();

  const [{ data, loading, error }, refetch] = useTCRCampaigns(tableOptions);
  const [{ data: providerData, loading: providerLoading, error: providerError }] = useProviders();

  const [{ data: createData, loading: createLoading, error: createError }, createTCRCampaign] = useAxios<
    IHttpResponse<void>
  >(
    {
      url: 'tcr-campaigns-create',
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: updateLoading }, updateTCRCampaign] = useAxios<IHttpResponse<void>>(
    {
      url: 'tcr-campaigns-update',
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: toggleLoading }, toggleTCRCampaign] = useAxios<IHttpResponse<void>>(
    {
      url: 'tcr-campaigns-update',
      method: 'POST',
    },
    { manual: true }
  );

  const tcrCampaigns: ITCRCampaignDetails[] =
    data?.data.records.map((res) => ({
      ...res,
      activeNumbers: parseInt(res.activeNumbers! as any),
    })) ?? [];

  const totalCount: number = data?.data.totalCount ?? 0;

  const providerOptions: IDropdownValue[] =
    providerData?.data.map((provider) => ({
      label: provider.name ?? '',
      value: provider.name ?? '',
    })) ?? [];

  tcrCampaignDropdownOptions.providerName = providerOptions;

  const handleSearchOptionChange = (searchOptions: ISearchRequest) => {
    setTableOptions(searchOptions);
  };

  useEffect(() => {
    handleRefetch();
  }, [tableOptions]);

  const columns = getColumns({
    tcrCampaignId: (item: ITCRCampaignDetails) => {
      openDetailsPanel(item);
    },
  });

  const handleRefetch = async () => {
    try {
      await refetch();
    } catch (err) {}
  };

  const getUpdateErrorMessage = (err: any): string =>
    err?.response?.data?.message && err?.response?.status < 500 ? err.response.data.message : defaultError;

  const handleToggle = async () => {
    const id = selectedItem?.id;
    if (!id) {
      addNotification({
        header: 'Error',
        content: 'No TCR campaign selected.',
        type: NotificationType.FAILURE,
      });
      return;
    }

    const wasActive = selectedItem.isActive ?? false;
    const payload: IUpdateTCRCampaignRequest = {
      id,
      isActive: !wasActive,
    };

    try {
      await toggleTCRCampaign({ data: payload });

      closeDetailsPanel();
      addNotification({
        header: `TCR Campaign ${!wasActive ? 'enabled' : 'disabled'} successfully.`,
      });
      void handleRefetch();
    } catch (err) {
      console.error(err);
      addNotification({
        header: 'Error',
        content: getUpdateErrorMessage(err),
        type: NotificationType.FAILURE,
      });
    }
  };

  const handleUpdate = async (formData: ITCRCampaignForm, force = false): Promise<void> => {
    const id = selectedItem?.id;
    if (!id) {
      addNotification({
        header: 'Error',
        content: 'No TCR campaign selected.',
        type: NotificationType.FAILURE,
      });
      return;
    }

    const data: IUpdateTCRCampaignRequest = force
      ? {
          id,
          campaignType: formData.campaignType,
          messageType: formData.messageType,
          force: true,
        }
      : {
          id,
          campaignType: formData.campaignType,
          messageType: formData.messageType,
        };

    try {
      await updateTCRCampaign({ data });

      closeDetailsPanel();
      setForceUpdateWarning('');
      setPendingUpdateData(undefined);
      addNotification({
        header: 'TCR Campaign updated successfully.',
      });
      void handleRefetch();
    } catch (err: any) {
      if (!force && err?.response?.status === 409) {
        setPendingUpdateData(formData);
        setForceUpdateWarning(err.response.data.message);
        return;
      }

      console.error(err);
      addNotification({
        header: 'Error',
        content: getUpdateErrorMessage(err),
        type: NotificationType.FAILURE,
      });
    }
  };

  const handleForceUpdate = async () => {
    if (pendingUpdateData) {
      await handleUpdate(pendingUpdateData, true);
    }
  };

  const openDetailsPanel = (item?: ITCRCampaignDetails) => {
    if (item) {
      setSelectedItem(item);
    }
    setShowDetailsPanel(true);
  };

  const closeDetailsPanel = () => {
    setSelectedItem(undefined);
    setShowDetailsPanel(false);
    setForceUpdateWarning('');
    setPendingUpdateData(undefined);
  };

  const handleSubmit = async (formData: ITCRCampaignForm): Promise<void> => {
    try {
      await createTCRCampaign({
        data: {
          clientId: formData.client?.value,
          providerId: formData.providerId,
          campaignType: formData.campaignType,
          messageType: formData.messageType,
        } as ITCRCampaignCreationRequest,
      });

      closeDetailsPanel();
      addNotification({
        header: 'TCR Campaign created successfully!',
      });
      void handleRefetch();
    } catch (err) {
      console.error(err);
      addNotification({
        header: 'Error',
        content: defaultError,
        type: NotificationType.FAILURE,
      });
    }
  };

  const filterColumns: IColumn[] = columns.filter((col) =>
    ['clientName', 'providerName', 'messageType', 'campaignType'].includes(col.fieldName)
  );

  const displayError = createError?.response?.data
    ? createError.response.data.statusCode < 500
      ? createError.response.data.message
      : defaultError
    : null;

  return (
    <>
      <h2 className="pb-2 dark:text-white">TCR Campaigns</h2>

      <div className="flex justify-end pb-2 space-x-2">
        <Button
          id="system-number-add"
          variant={ButtonVariantEnum.SECONDARY}
          onClick={() => {
            setShowDetailsPanel(true);
          }}
        >
          Add
        </Button>
      </div>

      <Table
        shimmer
        loading={loading}
        columns={columns}
        items={tcrCampaigns}
        count={totalCount}
        skipEnumFormattingColumns={['messageType']}
        enumExceptions={[{ fieldName: 'campaignType', exceptions: ['GOTV'] }]}
        tableSearchOptions={tableOptions}
        onSearchOptionChange={handleSearchOptionChange}
        paginate
        filter
        filterColumns={filterColumns}
        filterDropdownOptions={tcrCampaignDropdownOptions}
      />

      <TCRCampaignDetailsPanel
        show={showDetailsPanel}
        loading={createLoading}
        toggleLoading={toggleLoading}
        updateLoading={updateLoading}
        selectedItem={selectedItem}
        errorMessage={displayError}
        onClosePanel={closeDetailsPanel}
        handleSubmit={handleSubmit}
        handleToggle={handleToggle}
        handleUpdate={handleUpdate}
        forceUpdateWarning={forceUpdateWarning}
        onForceUpdate={handleForceUpdate}
        onDismissForceWarning={() => {
          setForceUpdateWarning('');
          setPendingUpdateData(undefined);
        }}
      />
    </>
  );
};

export default TCRCampaigns;
